// useEmployeeAllRegFormModal.js
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { post, get } from "@utils/axios_api";
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    file: yup.mixed()
        .required("엑셀 파일을 선택해주세요.")
        .test("fileSize", "파일 크기는 10MB 이하여야 합니다.", (value) => {
            return !value || (value[0] && value[0].size <= 10 * 1024 * 1024);
        })
        .test("fileType", "엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.", (value) => {
            return !value || (value[0] && /\.(xlsx|xls)$/i.test(value[0].name));
        })
});

export function useEmployeeAllRegFormModal({ onSuccess, onClose, compCode, siteCode }) {
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: { file: null },
        shouldFocusError: false,
    });

    const { handleSubmit, setValue, watch } = methods;
    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // 샘플 다운로드 기능
    const handleSampleDownload = async () => {
        try {
            const response = await get("/admin/employee/downloadSampleExcel", {
                responseType: "blob",
            });
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = "사원등록_샘플양식.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("샘플 양식 다운로드 오류:", error);
        }
    };

    // 미리보기 API 호출
    const previewUpload = async (formData) => {
        try {
            setLoading(true);
            setErrorMsg("");

            const response = await post("/admin/employee/uploadExcel", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.status !== false && response.result) {
                setPreviewData(response.result);
            } else {
                setErrorMsg(response.message || "미리보기 데이터를 불러오는데 실패했습니다.");
                setPreviewData([]);
            }
        } catch (error) {
            console.error("미리보기 API 오류:", error);

            // 서버에서 온 검증 오류 메시지 처리
            if (error.response && error.response.data && error.response.data.message) {
                setErrorMsg(error.response.data.message);
            } else {
                setErrorMsg("미리보기 처리 중 오류가 발생했습니다.");
            }
            setPreviewData([]);
        } finally {
            setLoading(false);
        }
    };

    // 미리보기 후 등록 처리
    const saveData = async () => {
        if (previewData.length === 0) {
            setErrorMsg("먼저 미리보기 후 등록할 데이터를 확인해주세요.");
            toast.warn("먼저 미리보기 후 등록할 데이터를 확인해주세요.");
            return;
        }

        if (!compCode) {
            toast.warn("회사를 선택해주세요.");
            return;
        }

        if (!siteCode) {
            toast.warn("사이트를 선택해주세요.");
            return;
        }

        try {
            setLoading(true);

            // 부서 관리와 동일한 구조로 데이터 전송
            const payload = {
                compCode: compCode,
                siteCode: siteCode,
                previewData: previewData
            };

            const response = await post("/admin/employee/employeeAllSave", payload);

            if (response.status !== false) {
                const result = response.result || {};
                const successCount = result.successCount ?? 0;
                const duplicatedPhones = result.duplicatedPhones || [];
                let message = `${successCount}건 등록 완료.`;
                if (duplicatedPhones.length > 0) {
                    message += ` 중복 연락처로 제외된 항목: ${duplicatedPhones.join(", ")}`;
                }
                toast.success(message);
                setPreviewData([]);
                methods.reset();
                if (onSuccess) onSuccess(response);
                if (onClose) onClose();
            } else {
                setErrorMsg(response.message || "등록에 실패하였습니다.");
                toast.error(response.message || "등록에 실패하였습니다.");
            }
        } catch (error) {
            console.error("등록 API 오류:", error);

            // 서버에서 온 에러 메시지 추출
            let errorMessage = "등록 처리 중 오류가 발생했습니다.";
            let isValidationError = false;

            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;
                
                if (status === 400 && data && data.message) {
                    // 400 Bad Request는 validation 에러
                    errorMessage = data.message;
                    isValidationError = true;
                } else if (status === 500 && data && data.message) {
                    // 500 에러에서도 구체적인 메시지가 있으면 사용
                    if (data.message.includes("시스템 오류") || data.message.includes("관리자에게 문의")) {
                        // 일반적인 시스템 오류 메시지인 경우
                        errorMessage = "데이터 처리 중 오류가 발생했습니다. 입력한 데이터를 확인해주세요.";
                    } else {
                        // 구체적인 에러 메시지가 있는 경우
                        errorMessage = data.message;
                        isValidationError = true;
                    }
                } else if (data && data.message) {
                    errorMessage = data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            setErrorMsg(errorMessage);
            
            // validation 에러는 에러 메시지만 표시

        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        const fileList = data.file;
        if (!fileList || fileList.length === 0) {
            toast.warn("업로드할 파일을 선택해주세요.");
            return;
        }

        if (!compCode) {
            toast.warn("회사를 선택해주세요.");
            return;
        }

        if (!siteCode) {
            toast.warn("사이트를 선택해주세요.");
            return;
        }

        const file = fileList[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("compCode", compCode);
        formData.append("siteCode", siteCode);

        await previewUpload(formData);
    };

    return {
        methods,
        handleFormSubmit: handleSubmit(onSubmit),
        previewData,
        saveData,
        loading,
        errorMsg,
        setPreviewData,
        handleSampleDownload,
        setValue,
        watch,
    };
}